import { Funeral, CremationStatus, DeathBenefitsStatus, CustomerEventType } from '../../types';
import { benefitsSettled } from '../caseClosure';
import { isPreplanOnly } from '../preneedMode';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProcessStep {
  id: string;
  label: string;
  /** lucide-react icon name */
  icon: string;
  /** Tailwind text-color class for the icon */
  color: string;
  /** If set, step only appears for these regions */
  regions?: ('quebec' | 'ontario')[];
  /** Floating steps reposition when incomplete */
  floating?: boolean;
  /** Which validation form this step relates to (for badges) */
  validationForm?: 'dec' | 'benefits';
  /** If set, step only appears when this condition returns true */
  condition?: (funeral: Funeral) => boolean;
  /** Check whether this step is complete */
  isComplete: (funeral: Funeral, events: FeedEvent[]) => boolean;
  /** Optional: return the timestamp when the step was completed */
  getCompletedAt?: (funeral: Funeral, events: FeedEvent[]) => string | null;
}

export interface PositionedStep extends ProcessStep {
  completed: boolean;
  completedAt: string | null;
}

export type FeedEvent = {
  id?: number | string;
  type: string;
  timestamp: string;
  [key: string]: any;
};

// ─── Cremation status ordering ────────────────────────────────────────────────

const CREMATION_STATUS_ORDER: string[] = [
  CremationStatus.ARRANGEMENT,
  CremationStatus.TRANSPORT_DISPATCHED,
  CremationStatus.CREMATION_SCHEDULED,
  CremationStatus.READY_FOR_DELIVERY,
  CremationStatus.DELIVERY_SCHEDULED,
  CremationStatus.DELIVERED,
];

export function isStatusAtLeast(current: string | undefined | null, target: string): boolean {
  if (!current) return false;
  const currentIdx = CREMATION_STATUS_ORDER.indexOf(current);
  const targetIdx = CREMATION_STATUS_ORDER.indexOf(target);
  if (currentIdx === -1 || targetIdx === -1) return false;
  return currentIdx >= targetIdx;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hasEvent(events: FeedEvent[], type: string): boolean {
  return events.some((e) => e.type === type);
}

function getEventTimestamp(events: FeedEvent[], type: string): string | null {
  const event = events.find((e) => e.type === type);
  return event?.timestamp ?? null;
}

function getStatusChangeTimestamp(events: FeedEvent[], status: string): string | null {
  const event = events.find((e) => e.type === CustomerEventType.STATUS_CHANGE && e.status === status);
  return event?.timestamp ?? null;
}

function hasDeliveryAddress(funeral: Funeral): boolean {
  const addr = funeral.delivery_address;
  if (!addr) return false;
  return !!(addr.address || addr.formatted_address || addr.civic_number);
}

// ─── Step definitions ─────────────────────────────────────────────────────────

export const ALL_STEPS: ProcessStep[] = [
  {
    id: 'quote_sent',
    label: 'Quote Sent',
    icon: 'FileText',
    color: 'text-indigo-600',
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.CONTRACT_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.CONTRACT_SENT),
  },
  {
    id: 'onboarding_part1',
    label: 'Onboarding Part 1',
    icon: 'ClipboardCheck',
    color: 'text-blue-600',
    isComplete: (f) => !!f.paperwork1_complete,
  },
  {
    id: 'contracts_sent',
    label: 'Contracts Sent',
    icon: 'Send',
    color: 'text-indigo-600',
    // Pre-plan only (T-172): no contract or payment expected.
    condition: (f) => !isPreplanOnly(f),
    isComplete: (f) => !!f.contracts_sent_at,
    getCompletedAt: (f) => f.contracts_sent_at ?? null,
  },
  {
    id: 'contracts_signed',
    label: 'Contracts Signed',
    icon: 'PenLine',
    color: 'text-emerald-600',
    condition: (f) => !isPreplanOnly(f),
    isComplete: (f) => !!f.contracts_signed_at,
    getCompletedAt: (f) => f.contracts_signed_at ?? null,
  },
  {
    id: 'payment_complete',
    label: 'Payment Complete',
    icon: 'CreditCard',
    color: 'text-emerald-500',
    condition: (f) => !isPreplanOnly(f),
    isComplete: (f) => !!f.payment_complete,
  },
  {
    id: 'sp3_received',
    label: 'SP3 Received',
    icon: 'FileCheck',
    color: 'text-violet-600',
    regions: ['quebec'],
    isComplete: (f) => !!f.sp3_received,
  },
  {
    id: 'send_attestation',
    label: 'Send Attestation',
    icon: 'Mail',
    color: 'text-blue-600',
    regions: ['quebec'],
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.ATTESTATION_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.ATTESTATION_SENT),
  },
  {
    id: 'transport_dispatched',
    label: 'Transport Dispatched',
    icon: 'Truck',
    color: 'text-amber-600',
    isComplete: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.TRANSPORT_DISPATCHED),
  },
  {
    // CREMATORIUM_DOCS_FOLLOWUP_PLAN §4. The backend read model is authoritative
    // (it knows each partner's requirements and what was sent); this local copy
    // is only the pre-response fallback, so it stays conservative: shown once
    // dispatched, done once the cremation is scheduled (Q5).
    id: 'partner_documents',
    label: 'Partner documents',
    icon: 'FileStack',
    color: 'text-amber-700',
    condition: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.TRANSPORT_DISPATCHED),
    isComplete: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.CREMATION_SCHEDULED),
  },
  {
    id: 'send_prep_sheet',
    label: 'Send Prep Sheet',
    icon: 'FileText',
    color: 'text-purple-600',
    regions: ['quebec'],
    condition: (f) => !!f.has_visitation,
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.PREP_SHEET_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.PREP_SHEET_SENT),
  },
  {
    id: 'cremation_scheduled',
    label: 'Cremation Scheduled',
    icon: 'Flame',
    color: 'text-orange-500',
    isComplete: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.CREMATION_SCHEDULED),
    getCompletedAt: (_f, events) => getStatusChangeTimestamp(events, CremationStatus.CREMATION_SCHEDULED),
  },
  {
    id: 'ready_for_delivery',
    label: 'Ready for Delivery',
    icon: 'PackageCheck',
    color: 'text-teal-600',
    isComplete: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.READY_FOR_DELIVERY),
  },
  {
    id: 'delivery_confirmed',
    label: 'Delivery Confirmed',
    icon: 'MapPin',
    color: 'text-blue-500',
    // An address on file is not a confirmation. This step called 402 cases
    // "Delivery Confirmed" on the strength of one, 60 of which have no day and
    // nothing confirmed at all. delivery_confirmed_at is the real answer; a day
    // keeps the 342 cases that were scheduled before that column existed.
    isComplete: (f) =>
      !!f.delivery_confirmed_at || (hasDeliveryAddress(f) && !!f.delivery_day),
  },
  {
    id: 'delivery_scheduled',
    label: 'Delivery Scheduled',
    icon: 'CalendarCheck',
    color: 'text-blue-600',
    isComplete: (f) => isStatusAtLeast(f.cremation_status, CremationStatus.DELIVERY_SCHEDULED),
  },
  {
    id: 'delivered',
    label: 'Delivered',
    icon: 'CircleCheckBig',
    color: 'text-emerald-600',
    isComplete: (f) => f.cremation_status === CremationStatus.DELIVERED,
  },
  {
    id: 'onboarding_part2',
    label: 'Onboarding Part 2',
    icon: 'ClipboardList',
    color: 'text-blue-600',
    floating: true,
    isComplete: (f) => !!f.paperwork2_complete,
  },
  {
    id: 'order_urn',
    label: 'Order Urn',
    icon: 'Package',
    color: 'text-amber-600',
    floating: true,
    // Only our own urn is bought from a supplier: the cardboard box ships with
    // the ashes and a family's own urn is already in their hands.
    condition: (f) => (f as any).urn_choice === 'cleo',
    // Anything past 'to_order' counts. The urn card carries the finer stages
    // (shipped, received); the step only asks whether it was ordered at all,
    // which is the part that used to be forgotten.
    isComplete: (f) =>
      ['ordered', 'shipped', 'received'].includes(
        ((f as any).urn_order_status ?? '') as string,
      ),
  },
  {
    id: 'dec_complete',
    label: 'DEC Complete',
    icon: 'FileSpreadsheet',
    color: 'text-violet-600',
    regions: ['quebec'],
    validationForm: 'dec',
    isComplete: (f) => !!f.dec_ready,
  },
  {
    id: 'benefits_complete',
    label: 'Benefits Complete',
    icon: 'Heart',
    color: 'text-rose-500',
    validationForm: 'benefits',
    isComplete: (f) => f.death_benefits_status === DeathBenefitsStatus.COMPLETED,
  },
  {
    id: 'paperwork_complete_notification',
    label: 'Send Confirmation Email',
    icon: 'Mail',
    color: 'text-emerald-600',
    regions: ['quebec'],
    // Surfaces once BOTH the DEC and the death-benefits application are settled
    // — the moment the family can be sent their confirmation numbers. Uses
    // `benefitsSettled` (not `=== COMPLETED`) so 'ineligible' and mailed-packet
    // cases get the step too; they used to close with no way to send from here.
    condition: (f) => !!f.dec_ready && benefitsSettled(f),
    // "Close without email" counts as done — otherwise a case that shouldn't be
    // emailed shows an outstanding step forever.
    isComplete: (f, events) =>
      hasEvent(events, CustomerEventType.PAPERWORK_COMPLETE_EMAIL_SENT) ||
      !!f.paperwork_email_waived_at,
    getCompletedAt: (f, events) =>
      getEventTimestamp(events, CustomerEventType.PAPERWORK_COMPLETE_EMAIL_SENT) ||
      f.paperwork_email_waived_at ||
      null,
  },
  {
    id: 'send_benefits_to_service_canada',
    label: 'Send Benefits to Service Canada',
    icon: 'Send',
    color: 'text-rose-500',
    // Show only after the customer has signed at least one CPP form via DocuSeal.
    condition: (f) =>
      Array.isArray((f as any).cpp_benefits_signed_urls) &&
      (f as any).cpp_benefits_signed_urls.length > 0,
    isComplete: (f) => (f as any).cpp_benefits_mailing_status === 'sent',
    getCompletedAt: (f) => (f as any).cpp_benefits_mailed_at ?? null,
  },
  {
    id: 'send_thank_you_card',
    label: 'Send Thank You Card',
    icon: 'Heart',
    color: 'text-pink-500',
    floating: true,
    condition: (f) => !!(f as any).thank_you_card,
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.THANK_YOU_CARD_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.THANK_YOU_CARD_SENT),
  },
  {
    id: 'review_request_sent',
    label: 'Review Request Sent',
    icon: 'Star',
    color: 'text-amber-500',
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.REVIEW_REQUEST_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.REVIEW_REQUEST_SENT),
  },
  {
    id: 'aftercare_email_sent',
    label: 'Aftercare Email',
    icon: 'Heart',
    color: 'text-rose-500',
    condition: (f) => f.cremation_status === CremationStatus.DELIVERED,
    isComplete: (_f, events) => hasEvent(events, CustomerEventType.AFTERCARE_EMAIL_SENT),
    getCompletedAt: (_f, events) => getEventTimestamp(events, CustomerEventType.AFTERCARE_EMAIL_SENT),
  },
];

// ─── Region filtering ─────────────────────────────────────────────────────────

export function getStepsForRegion(region: string | undefined, funeral?: Funeral): ProcessStep[] {
  const r = region || 'quebec'; // default to quebec
  return ALL_STEPS.filter((step) => {
    if (step.regions && !step.regions.includes(r as any)) return false;
    if (step.condition && funeral && !step.condition(funeral)) return false;
    return true;
  });
}

// ─── Floating step repositioning ──────────────────────────────────────────────

export function getPositionedSteps(
  steps: ProcessStep[],
  funeral: Funeral,
  events: FeedEvent[],
): PositionedStep[] {
  // Evaluate completion for all steps
  const evaluated = steps.map((step) => ({
    ...step,
    completed: step.isComplete(funeral, events),
    completedAt: step.getCompletedAt?.(funeral, events) ?? null,
  }));
  return positionSteps(evaluated);
}

/**
 * The floating-step repositioning, split out so callers that already have
 * evaluated steps (e.g. the backend `/cases/:id/state` read model) can reuse it
 * without re-evaluating `isComplete` locally.
 */
export function positionSteps(evaluated: PositionedStep[]): PositionedStep[] {
  // Separate floating from non-floating
  const floating = evaluated.filter((s) => s.floating);
  const fixed = evaluated.filter((s) => !s.floating);

  // For each floating step: if complete, keep at natural position; if not, move after last completed fixed step
  const result: PositionedStep[] = [...fixed];

  for (const floatStep of floating) {
    if (floatStep.completed) {
      // Find its natural position (index in original evaluated array) and insert there
      const naturalIdx = evaluated.indexOf(floatStep);
      // Find the right insertion point in result
      let insertIdx = 0;
      for (let i = 0; i < result.length; i++) {
        const origIdx = evaluated.indexOf(result[i]);
        if (origIdx < naturalIdx) insertIdx = i + 1;
      }
      result.splice(insertIdx, 0, floatStep);
    } else {
      // Find last completed non-floating step
      let lastCompletedIdx = -1;
      for (let i = 0; i < result.length; i++) {
        if (result[i].completed) lastCompletedIdx = i;
      }
      // Insert right after the last completed step
      result.splice(lastCompletedIdx + 1, 0, floatStep);
    }
  }

  return result;
}
